import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Crown, Users, Building, DollarSign, ShieldCheck } from 'lucide-react';
import {
  KPICard, AgreementBlock, ComplianceBlock, ActivityBlock, NPSBlock, EverperformBlock,
  PerformanceRatingBlock, RatingPill,
  fmt, fmtCompact, fmtPct, Section,
} from './people/DeepDiveSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamLeaderDeepDive = ({ initialLeader, onConsumed }) => {
  const [leaders, setLeaders] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(async () => {
    const res = await axios.get(`${API}/team-leader-deep-dive/list`);
    setLeaders(res.data.teamLeaders);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!leaders.length) return;
    if (initialLeader && leaders.some(l => l.teamLeader === initialLeader)) {
      setSelected(initialLeader);
      if (onConsumed) onConsumed();
    } else if (!selected) {
      setSelected(leaders[0].teamLeader);
    }
  }, [leaders, initialLeader, selected, onConsumed]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    axios.get(`${API}/team-leader-deep-dive`, { params: { teamLeader: selected } })
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div data-testid="team-leader-deep-dive" className="space-y-6">
      {/* Picker */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-[10px] bg-[#171717] flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-[250px]">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">Team Leader</p>
            <select
              data-testid="team-leader-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-[8px] border border-[#E5E5E5] text-sm font-medium focus:outline-none focus:border-[#171717]"
            >
              {leaders.map(l => (
                <option key={l.teamLeader} value={l.teamLeader}>
                  {l.teamLeader} · {l.managerCount} managers · {l.totalPlans} plans · {fmtCompact(l.totalRevenue)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center h-96" data-testid="team-leader-deep-dive-loading">
          <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Performance Rating */}
          <PerformanceRatingBlock performanceRating={data.performanceRating} subjectLabel="Team" />

          {/* Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPICard icon={Users} label="Managers in Team" value={data.managerCount} testId="tldd-managers" />
            <KPICard icon={Building} label="Total Plans" value={data.planCount} testId="tldd-plans" />
            <KPICard icon={DollarSign} label="Team Revenue" value={fmtCompact(data.totalRevenue)} testId="tldd-revenue" />
            <KPICard icon={Building} label="Lots Managed" value={data.totalLots.toLocaleString()} testId="tldd-lots" />
            <KPICard icon={ShieldCheck} label="Avg Compliance" value={data.avgComplianceScore != null ? `${data.avgComplianceScore}` : '—'} sublabel="of 100" testId="tldd-compliance" />
          </div>

          <AgreementBlock agreement={data.agreement} />
          <ComplianceBlock compliance={data.compliance} />
          <ActivityBlock activity={data.activity} />
          <EverperformBlock everperform={data.everperform} />
          <NPSBlock nps={data.nps} />

          {/* Per-manager breakdown */}
          <Section title="Per-Manager Breakdown" icon={Users} subtitle="How each direct report contributes to the team's roll-up.">
            <div className="bg-white border border-[#E5E5E5] rounded-[10px] overflow-hidden">
              <table className="w-full" data-testid="tldd-breakdown">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Manager</th>
                    <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Rating</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Plans</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Lots</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Revenue</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Under Contract %</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Revenue at Risk</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Compliance</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">NPS 7+%</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Emails</th>
                    <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {data.managerBreakdown.map(m => (
                    <tr key={m.manager} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/40">
                      <td className="py-2 px-3 text-sm text-[#171717] font-medium">{m.manager}</td>
                      <td className="py-2 px-3"><RatingPill ratingKey={m.performanceRating?.ratingKey || 'pending'} size="xs" /></td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.planCount}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.totalLots?.toLocaleString() || '—'}</td>
                      <td className="py-2 px-3 text-sm text-right font-semibold text-[#171717]">{fmtCompact(m.totalRevenue)}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.pctUnderContract != null ? `${m.pctUnderContract}%` : '—'}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.revenueAtRisk != null ? fmtCompact(m.revenueAtRisk) : '—'}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.avgComplianceScore ?? '—'}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.ratedSevenPlusPct != null ? `${m.ratedSevenPlusPct}%` : '—'}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.messagesReceived?.toLocaleString() || '—'}</td>
                      <td className="py-2 px-3 text-sm text-right text-[#525252]">{m.callsTotal?.toLocaleString() || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Peer comparison */}
          {data.peerComparison?.length > 0 && (
            <Section title="Team Leader Peer Comparison" icon={Crown}>
              <div className="bg-white border border-[#E5E5E5] rounded-[10px] overflow-hidden">
                <table className="w-full" data-testid="tldd-peers">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Team Leader</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Managers</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Plans</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Revenue</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.peerComparison.map(p => (
                      <tr key={p.teamLeader} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/40">
                        <td className="py-2 px-3 text-sm text-[#171717]">{p.teamLeader}</td>
                        <td className="py-2 px-3 text-sm text-right text-[#525252]">{p.managerCount}</td>
                        <td className="py-2 px-3 text-sm text-right text-[#525252]">{p.planCount}</td>
                        <td className="py-2 px-3 text-sm text-right font-semibold text-[#171717]">{fmtCompact(p.totalRevenue)}</td>
                        <td className="py-2 px-3 text-sm text-right text-[#525252]">{p.avgComplianceScore}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default TeamLeaderDeepDive;
